import { KameleoonClient, CustomData } from '@kameleoon/javascript-sdk'
import secrets from '@/modules/config/secrets'
import KameleoonApi from '@/modules/api/kameleoon'
import { notifyBugsnag } from '@/modules/services/bugsnag'

// https://developers.kameleoon.com/feature-management-and-experimentation/web-sdks/js-sdk/#reference

const { kameleoonUpdateInterval, env, isMobile } = window.vueSupport

const customDataKeyIndices = {
  tier: 0,
  country: 3,
  platform: 5,
  app_platform: 6,
  version: 7,
  key: 8,
  brand_code: 9,
  page_path: 10,
  hashed_card_number: 11,
  card_number: 12,
  component: 13,
  retail_orders_count: 16,
  ecomm_orders_count: 17,
  app_orders_count: 18,
  engaged_browse_session: 20,
  product_exploration_session: 21,
  category: 23,
  brand_name: 24,
  internal_name: 25,
  entry_id: 26,
  content_parent_name: 27
}

const GoalIdMap = {
  PDP_VIEW: 361146,
  ADD_TO_CART: 361147,
  PURCHASE: 361148,
  WISHLIST: 413581,
  IMPRESSION: 360018,
  CLICK: 359094
}

const kameleoonApi = new KameleoonApi()

class Kameleoon {
  #client
  #visitorCode
  #isSDKReady = false

  constructor () {
    this.#client = new KameleoonClient({
      siteCode: secrets.kameleoonSiteCode,
      configuration: {
        // In minutes, in-sync with backend interval
        updateInterval: kameleoonUpdateInterval || 5,
        // Maps to our Kameleoon environments 'production', 'staging', 'development'
        environment: env
      }
    })
    this.initialize()
  }

  async initialize () {
    try {
      await this.#client.initialize()

      this.#isSDKReady = true

      // If the cookie does not exist Kameleoon will generate and store it automatically
      // If the cookie already exists Kameleoon will fetch the previous value already stored
      this.#visitorCode = this.#client.getVisitorCode()

      const country = window.I18n.siteCountryLs.toUpperCase()
      const platform = isMobile ? 'mobile_web' : 'web'
      this.#client.addData(
        this.#visitorCode,
        new CustomData(customDataKeyIndices.country, country)
      )
      this.#client.addData(
        this.#visitorCode,
        new CustomData(customDataKeyIndices.platform, platform)
      )

      // https://developers.kameleoon.com/feature-management-and-experimentation/web-sdks/js-sdk/#flush
      // Skip calling client.flush as the SDK will automatically sync periodically,
      // but we should manually call flush if we need immediate syncing for some reason
    } catch (error) {
      console.error('Could not initialize Kameleoon client', error)
    }
  }

  get client () {
    return this.#client
  }

  immediateSync () {
    this.#client.flush()
  }

  isFeatureFlagActive (featureFlagName = '') {
    if (!this.#isSDKReady) {
      console.error('Kameleoon client is not ready')
      return false
    }

    return this.#client.isFeatureFlagActive(this.#visitorCode, featureFlagName)
  }

  trackConversion ({
    goalKey = '',
    revenue = null,
    goalId = null,
    metadata = {},
    useServer = false,
    ...params
  }) {
    if (useServer) {
      kameleoonApi
        .trackEvent({ goalKey, revenue, goalId, metadata, params })
        .catch((error) => this._notifyError(error))
      return
    }

    if (!this.#isSDKReady) {
      console.error('Kameleoon client is not ready')
      return
    }
    try {
      if ((goalKey && !GoalIdMap[goalKey]) || (!goalId && !goalKey)) {
        throw new Error(
          `[Kameleoon] Invalid goalKey/Missing goalId: ${goalKey ||
            goalId}. Must provide a valid goalKey or goalId to track conversion.`
        )
      }

      this.#client.trackConversion({
        visitorCode: this.#visitorCode,
        goalId: goalId || GoalIdMap[goalKey],
        revenue,
        metadata: Object.entries(metadata || {}).reduce((acc, [key, value]) => {
          const keyIndex = customDataKeyIndices[key]
          const values = Array.isArray(value)
            ? value.map(String)
            : value != null && value !== undefined
            ? [String(value)]
            : []

          if (keyIndex >= 0) acc.push(new CustomData(keyIndex, ...values))
          return acc
        }, [])
      })
    } catch (error) {
      this._notifyError(error)
    }
  }

  _notifyError (error) {
    try {
      notifyBugsnag(error)
    } catch (notifyError) {
      console.error(
        'Failed to notify Bugsnag about Kameleoon error',
        notifyError
      )
    }
  }
}

export default new Kameleoon()
